<template>
  <div
    :aria-hidden="areaHidden"
    class="dialog"
    role="dialog"
    aria-labelledby="dialog-title"
    aria-describedby="dialog-description"
  >
    <div class="dialog__container">
      <form @submit.prevent="onSubmit">
        <div class="search-wrapper">
          <div class="select-wrapper">
            <kcs-select v-model="dataOrder" :options="orderList" name="order" />
          </div>
          <template v-if="isDispEventType">
            <h2>イベントの規模・種類から探す</h2>
            <div class="checkbox-wrapper">
              <span
                v-for="(facilityEventType, index) in facilityEventTypes"
                :key="index"
              >
                <kcs-dl-checkbox
                  v-if="isDispEventTypeOption(index)"
                  v-model="dataEventType"
                  :title="facilityEventType.title"
                  :options="eventTypeOptions[index]"
                  name="eventType"
                />
              </span>
            </div>
          </template>
          <template v-if="isDispService">
            <h2>設備・サービスから探す</h2>
            <div class="facility-wrapper checkbox-wrapper">
              <kcs-ul-checkbox
                v-model="dataService"
                :options="serviceOptions"
                name="service"
              />
            </div>
          </template>
          <template v-if="isDispFacilityType">
            <h2>施設の種類から探す</h2>
            <div class="facility-wrapper checkbox-wrapper">
              <kcs-ul-checkbox
                v-model="dataFacilityType"
                :options="facilityTypeOptions"
                name="facilityType"
              />
            </div>
          </template>
          <template v-if="isDispArea">
            <h2>エリアから探す</h2>
            <div class="search-wrapper">
              <div class="select-wrapper">
                <kcs-select
                  v-model="dataArea"
                  :options="areaOptions"
                  name="area"
                />
              </div>
            </div>
          </template>
          <template v-if="isDispStation">
            <h2>最寄駅から探す</h2>
            <div class="search-wrapper">
              <div class="select-wrapper">
                <kcs-select
                  v-model="dataStation"
                  :options="stationOptions"
                  name="station"
                />
              </div>
            </div>
          </template>
        </div>
        <div class="button-wrapper">
          <kcs-search-button
            @click.native="
              handleAnalyticsTrackEvent('search', 'submit', 'filter', 1)
            "
            name="検索する"
          />
        </div>
      </form>
      <button
        @click="closeSearchFilter"
        type="button"
        aria-label="Close Navigation"
        class="close-dialog modal__close"
      >
        ×
      </button>
    </div>
    <div :aria-hidden="areaHidden" class="dialog-overlay" tabindex="-1"></div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { config } from '@/utils/config'
import { createUrl } from '@/utils/create-url'
import { methods } from '@/utils/methods'
import KcsSelect from '@/components/atoms/kcs-select.vue'
import kcsDlCheckbox from '@/components/atoms/kcs-dl-checkbox.vue'
import kcsUlCheckbox from '@/components/atoms/kcs-ul-checkbox.vue'
import KcsSearchButton from '@/components/atoms/kcs-search-button.vue'
export default {
  components: {
    KcsSelect,
    kcsDlCheckbox,
    kcsUlCheckbox,
    KcsSearchButton
  },
  data() {
    return {
      dataArea: '',
      dataEventType: [],
      dataService: [],
      dataFacilityType: [],
      dataOrder: '',
      dataStation: ''
    }
  },
  computed: {
    ...mapGetters({
      query: 'search_condition/query',
      openSearchFilter: 'search_condition/openSearchFilter',
      facilityList: 'facility/list'
    }),
    areaHidden() {
      return this.openSearchFilter ? 'false' : 'true'
    },
    // 並び順マスタをセット
    orderList() {
      const list = [...config.orderList]
      return list
    },
    // イベント規模・種類マスタをセット
    facilityEventTypes() {
      return config.facilityEventTypes
    },
    areaMode() {
      return methods.getAreaMode(this.facilityList.facet)
    },
    // イベント種別・規模の選択肢リストをセット
    eventTypeOptions() {
      // ファセット情報を整形
      const eventType = methods.formatFacet(
        this.facilityList.facet.event_type,
        this.query,
        'eventType'
      )
      return methods.getEventTypeOptions(eventType)
    },
    // サービスの選択肢リストをセット
    serviceOptions() {
      // ファセット情報を整形
      const service = methods.formatFacet(
        this.facilityList.facet.service,
        this.query,
        'service'
      )
      return methods.getServiceOptions(service)
    },
    facilityTypeOptions() {
      // ファセット情報を整形
      const facilityType = methods.formatFacet(
        this.facilityList.facet.facility_type,
        this.query,
        'facilityType'
      )
      return methods.getFacilityTypeOptions(facilityType)
    },
    // エリアの選択肢リストをセット
    areaOptions() {
      const areaOptions = methods.getAreaOptions(
        this.facilityList.facet[this.areaMode]
      )

      // 配列の最初に追加
      switch (this.areaMode) {
        case 'area':
          areaOptions.unshift({ name: 'エリア', value: '' })
          break
        case 'prefecture':
          areaOptions.unshift({ name: '都道府県', value: '' })
          break
        case 'city':
          areaOptions.unshift({ name: '市区町村', value: '' })
          break
      }

      return areaOptions
    },
    // 最寄駅の選択肢リストをセット
    stationOptions() {
      const stationOptions = methods.getStationOptions(
        this.facilityList.facet.stations
      )
      // 配列の最初に追加
      stationOptions.unshift({ name: '最寄駅', value: '' })
      return stationOptions
    },
    // イベント種別・規模部分を表示するかどうか
    isDispEventType() {
      return (
        this.facilityList.facet.event_type &&
        this.facilityList.facet.event_type.length
      )
    },
    // サービス部分を表示するかどうか
    isDispService() {
      return (
        this.facilityList.facet.service &&
        this.facilityList.facet.service.length
      )
    },
    // 施設の種類部分を表示するかどうか
    isDispFacilityType() {
      return (
        this.facilityList.facet.facility_type &&
        this.facilityList.facet.facility_type.length
      )
    },
    // エリア部分を表示するかどうか
    isDispArea() {
      return (
        this.facilityList.facet[this.areaMode] &&
        this.facilityList.facet[this.areaMode].length
      )
    },
    // 最寄駅部分を表示するかどうか
    isDispStation() {
      return (
        this.facilityList.facet.stations &&
        this.facilityList.facet.stations.length
      )
    }
  },
  watch: {
    /* istanbul ignore next */
    $route(to, from) {
      // パラメーター変更を検知して、data変数を最新化
      this.setData()
    }
  },
  mounted() {
    this.setData()
  },
  methods: {
    onSubmit() {
      const query = {
        eventType: this.dataEventType,
        service: this.dataService,
        facilityType: this.dataFacilityType,
        order: this.dataOrder,
        keyword: this.query.keyword,
        eventTag: this.query.eventTag,
        station: this.dataStation
      }

      // (station, s)どちらから来たかによって保持しているものが違う
      if (this.query.station === '' && this.dataStation) {
        query.area = methods.getArea(this.query.prefecture)
        const s = methods.getStationAndLineName(this.dataStation)
        query.line = s.line
        query.station = s.station
      } else {
        query.area = this.dataArea
        query.line = this.query.line
        query.station = this.dataStation
      }

      query[this.areaMode] = this.dataArea
      if (this.areaMode === 'city') {
        query.prefecture = this.query.prefecture
      }

      this.$router.push(createUrl.createSearchUrl(query))
      this.closeSearchFilter()
    },
    setData() {
      this.dataOrder = this.query.order
      this.dataArea = this.query[this.areaMode]
      this.dataEventType = this.query.eventType
      this.dataService = this.query.service
      this.dataFacilityType = this.query.facilityType
      this.dataStation = this.query.station
    },
    // モーダルを閉じる
    closeSearchFilter() {
      this.$store.dispatch('search_condition/openSearchFilter', false)
    },
    // イベント種別・規模の選択肢が存在するかどうか
    isDispEventTypeOption(index) {
      return this.eventTypeOptions[index].length
    }
  }
}
</script>
